/**
===================================================
Module: frontend.src.components.CreateSessionModal.tsx
Description: Modal component for creating new VSLAM sessions with file upload
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useEffect } from "react";
import Uppy from "@uppy/core";
import { CreateSessionRequest } from "../types";
import { UPLOAD_CONSTRAINTS } from "../services/api";
import { useSessionCreationStore } from "../stores/sessionCreationStore";
import {
  extractVideoThumbnail,
  convertDataUrlToBlob,
} from "../utils/videoThumbnail";
import "@uppy/core/dist/style.css";

interface CreateSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNext: (
    sessionData: CreateSessionRequest,
    selectedFile: File,
    thumbnailBlob: Blob,
  ) => void;
  isUploading: boolean;
  persistState?: boolean; // New prop to control state persistence
}

const CreateSessionModal: React.FC<CreateSessionModalProps> = ({
  isOpen,
  onClose,
  onNext,
  isUploading,
  persistState = false, // Default to false (always reset state)
}) => {
  // Local state for form inputs
  const [newSession, setNewSession] = useState<CreateSessionRequest>({
    name: "",
    description: "",
    video_filename: "",
    video_size_bytes: 0,
  });
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [isClosing, setIsClosing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uppy, setUppy] = useState<Uppy | null>(null);
  const [thumbnailBlob, setThumbnailBlob] = useState<Blob | null>(null);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);

  // Zustand store for session creation state
  const {
    sessionData: storeSessionData,
    videoFile: storeVideoFile,
    setVideoFile,
    setThumbnailBlob: setStoreThumbnailBlob,
    setIsGeneratingThumbnail: setStoreIsGeneratingThumbnail,
    resetState,
  } = useSessionCreationStore();

  // Reset state when modal opens - but respect persistState parameter
  useEffect(() => {
    if (isOpen) {
      if (persistState && storeSessionData) {
        // Preserve session metadata (name, description)
        setNewSession({
          name: storeSessionData.name,
          description: storeSessionData.description,
          video_filename: "",
          video_size_bytes: 0,
        });

        // Reset video-related state
        setSelectedVideoFile(null);
        setThumbnailBlob(null);
        setIsGeneratingThumbnail(false);

        // Clear video-related store state but keep session data
        setVideoFile(null);
        setStoreThumbnailBlob(null);
        setStoreIsGeneratingThumbnail(false);
      } else {
        // Reset Zustand store
        resetState();

        // Reset local form state
        setNewSession({
          name: "",
          description: "",
          video_filename: "",
          video_size_bytes: 0,
        });
        setSelectedVideoFile(null);
        setThumbnailBlob(null);
        setIsGeneratingThumbnail(false);
      }

      setError("");
      setIsClosing(false);

      // Initialize Uppy
      initializeUppy();
    }
  }, [
    isOpen,
    persistState,
    storeSessionData,
    resetState,
    setVideoFile,
    setStoreThumbnailBlob,
    setStoreIsGeneratingThumbnail,
  ]);

  // Also reset store when component unmounts
  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  // Initialize Uppy instance
  const initializeUppy = () => {
    const uppyInstance = new Uppy({
      restrictions: {
        maxFileSize: UPLOAD_CONSTRAINTS.MAX_FILE_SIZE_GB * 1024 * 1024 * 1024,
        maxNumberOfFiles: UPLOAD_CONSTRAINTS.MAX_NUMBER_OF_FILES,
        allowedFileTypes: UPLOAD_CONSTRAINTS.ALLOWED_FILE_TYPES,
      },
      autoProceed: false,
    });

    uppyInstance.on("file-added", handleFileAdded);
    uppyInstance.on("file-removed", handleFileRemoved);

    setUppy(uppyInstance);

    return () => {
      if (uppyInstance) {
        uppyInstance.destroy();
      }
    };
  };

  const handleFileAdded = async (file: any) => {
    // Update local form state
    setNewSession((prev) => ({
      ...prev,
      video_filename: file.name || "video.mp4",
      video_size_bytes: file.size || 0,
    }));

    // Store video file locally and in Zustand store
    const videoFile = file.data as File;
    setSelectedVideoFile(videoFile);
    setVideoFile(videoFile);
    setError("");

    // Generate thumbnail
    setIsGeneratingThumbnail(true);
    setStoreIsGeneratingThumbnail(true);

    try {
      const thumbnailDataUrl = await extractVideoThumbnail(videoFile);
      const thumbnail = convertDataUrlToBlob(thumbnailDataUrl);

      // Store thumbnail locally and in Zustand store
      setThumbnailBlob(thumbnail);
      setStoreThumbnailBlob(thumbnail);
    } catch (error) {
      setError("Failed to generate video thumbnail");
    } finally {
      setIsGeneratingThumbnail(false);
      setStoreIsGeneratingThumbnail(false);
    }
  };

  const handleFileRemoved = () => {
    // Clear local form state
    setNewSession((prev) => ({
      ...prev,
      video_filename: "",
      video_size_bytes: 0,
    }));

    // Clear local state
    setSelectedVideoFile(null);
    setThumbnailBlob(null);
    setIsGeneratingThumbnail(false);

    // Clear Zustand store state
    setVideoFile(null);
    setStoreThumbnailBlob(null);
    setStoreIsGeneratingThumbnail(false);

    setError("");
  };

  const handleInputChange = (
    field: keyof CreateSessionRequest,
    value: string,
  ) => {
    setNewSession((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    if (!newSession.name.trim()) {
      setError("Please enter a session name.");
      return;
    }

    if (!selectedVideoFile) {
      setError("Please select a video file.");
      return;
    }

    if (!thumbnailBlob) {
      setError("Please wait for thumbnail generation to complete.");
      return;
    }

    // Pass data to parent component
    onNext(newSession, selectedVideoFile, thumbnailBlob);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      // Reset all local state
      setNewSession({
        name: "",
        description: "",
        video_filename: "",
        video_size_bytes: 0,
      });
      setSelectedVideoFile(null);
      setThumbnailBlob(null);
      setIsGeneratingThumbnail(false);
      setError("");
      setIsClosing(false);

      // ALWAYS reset Zustand store when closing
      resetState();

      // Clean up Uppy
      if (uppy) {
        uppy.getFiles().forEach((file) => uppy.removeFile(file.id));
      }

      onClose();
    }, 300);
  };

  const handleFileSelect = () => {
    if (uppy) {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".mp4,video/mp4";
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          uppy.addFile({
            name: file.name,
            type: file.type,
            data: file,
            size: file.size,
          });
        }
      };
      input.click();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const videoFile = files.find(
      (file) => file.type === "video/mp4" || file.name.endsWith(".mp4"),
    );

    if (videoFile && uppy) {
      uppy.addFile({
        name: videoFile.name,
        type: videoFile.type,
        data: videoFile,
        size: videoFile.size,
      });
    }
  };

  const removeFile = () => {
    if (uppy) {
      uppy.getFiles().forEach((file) => uppy.removeFile(file.id));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className={`modal-content ${isClosing ? "closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">ファイル追加</h2>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label className="form-label">
              ファイル名 <span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={newSession.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="ファイル名を入力してください"
              required
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label className="form-label">説明</label>
            <textarea
              className="form-textarea"
              value={newSession.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="ファイルの説明を入力してください"
              maxLength={500}
              rows={2}
            />
            <div className="character-count">
              {newSession.description?.length || 0}/500文字
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              動画ファイル(.mp4, 最大{UPLOAD_CONSTRAINTS.MAX_FILE_SIZE_GB}GB){" "}
              <span className="required">*</span>
            </label>
            <div className="custom-upload-area">
              {!selectedVideoFile ? (
                <div
                  className={`upload-zone ${isDragOver ? "drag-over" : ""}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleFileSelect}
                >
                  <div className="upload-icon">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M17 8L12 3L7 8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 3V15"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="upload-text">
                    <span className="upload-title">ここにファイルをドラッグ＆ドロップ</span>
                    <span className="upload-subtitle">またはクリックしてファイルを指定</span>
                  </div>
                </div>
              ) : (
                <div className="file-selected">
                  <div className="file-info">
                    <div className="file-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14 2V8H20"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="file-details">
                      <div className="file-name">{selectedVideoFile.name}</div>
                      <div className="file-size">
                        {(
                          (selectedVideoFile.size || 0) /
                          1024 /
                          1024 /
                          1024
                        ).toFixed(2)}{" "}
                        GB
                      </div>
                    </div>
                    <button className="remove-file" onClick={removeFile}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18M6 6L18 18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
            >
              キャンセル
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={
                !selectedVideoFile ||
                !newSession.name.trim() ||
                isUploading ||
                !thumbnailBlob
              }
            >
              次へ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSessionModal;
